import React, { useEffect } from "react";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { ListItemIcon, toast } from "@outpave/ds";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Stack } from "components/layout/reusableLayouts";
import StatusPage from "components/common/StatusPage";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_COMPANY,
  CHECK_ACTIVATION_COMPANY_STATUS,
} from "../graphql/company";
import { Status } from "../enums/Company";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ApplicationStatus from "enums/ApplicationStatus";
import { useBrandingContext } from "context/BrandingContext";
import {
  CARD_PROGRAMS_PATH,
  cardProgramUploadPath,
} from "utils/cardPrograms/cardProgramPaths";

const Pending: React.FC = () => {
  const [appStatus, setAppStatus] = React.useState(ApplicationStatus.PENDING);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A second issuer application belongs to one connection; the company's own
  // first one names none.
  const connectionId = searchParams.get("connectionId");
  const { t } = useTranslation();
  const { platform } = useBrandingContext();
  const platformName = platform?.name || "";

  const {
    data: companyData,
    loading: loadingCompany,
    startPolling,
    stopPolling,
  } = useQuery(GET_COMPANY, {
    fetchPolicy: "network-only",
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const companyStatus = companyData?.company?.status;
  const isSetupInProgress = companyStatus === Status.Pending;

  const [checkActivationStatus, { data: applicationData }] = useLazyQuery(
    CHECK_ACTIVATION_COMPANY_STATUS,
    {
      fetchPolicy: "network-only",
      pollInterval: 10000,
      variables: { connectionId },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  );

  useEffect(() => {
    if (!companyData) return;
    if (isSetupInProgress) return;
    if (appStatus !== ApplicationStatus.PENDING) return;
    const applicationStatus = applicationData?.checkActivationStatus?.status;
    if (
      applicationStatus === ApplicationStatus.DENIED ||
      applicationStatus === ApplicationStatus.CLOSED
    ) {
      setAppStatus(ApplicationStatus.DENIED);
      toast.error(t("Application Could Not Be Processed"));
      // A second issuer turning the company down leaves the company active.
      navigate(connectionId ? CARD_PROGRAMS_PATH : "/declined");
    } else if (applicationStatus === ApplicationStatus.APPROVED) {
      toast.success(t("Application Approved"));
      toast.success(t("Creating Account"));
      setAppStatus(ApplicationStatus.APPROVED);
    } else if (applicationStatus === ApplicationStatus.IN_REVIEW) {
      navigate(connectionId ? cardProgramUploadPath(connectionId) : "/upload");
    }

    checkActivationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData, checkActivationStatus, companyData, isSetupInProgress]);

  useEffect(() => {
    if (appStatus !== ApplicationStatus.APPROVED) return;
    startPolling(10000);
    if (companyStatus === Status.Active) {
      stopPolling();
      navigate(connectionId ? CARD_PROGRAMS_PATH : "/admin/dashboard");
    } else if (companyStatus === Status.Inactive) {
      stopPolling();
      navigate("/inactive");
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatus, companyStatus]);

  if (loadingCompany && !companyData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (appStatus === ApplicationStatus.APPROVED) {
    return (
      <StatusPage
        title={t("ApprovedTitle", { platformName })}
        message={t("ApprovedDescription")}
        icon={CheckmarkCircle02Icon}
        tone="success"
        action={{
          label: t("Get started"),
          onClick: () => navigate("/admin/dashboard"),
        }}
      />
    );
  }

  const whatHappensNext = [
    {
      icon: <GppGoodOutlinedIcon />,
      title: t("IdentityVerificationTitle"),
      description: t("IdentityVerificationDescription"),
    },
    {
      icon: <AccessTimeOutlinedIcon />,
      title: t("ComplianceReviewTitle"),
      description: t("ComplianceReviewDescription"),
    },
    {
      icon: <MailOutlineIcon />,
      title: t("DecisionActivationTitle"),
      description: t("DecisionActivationDescription"),
    },
  ];

  return (
    <StatusPage
      title={t("Application submitted")}
      message={t(
        connectionId
          ? "CardProgramApplicationSubmittedDescription"
          : "ApplicationSubmittedDescription",
      )}
      icon={CheckmarkCircle02Icon}
      action={
        connectionId
          ? {
              label: t("Back to card programs"),
              onClick: () => navigate(CARD_PROGRAMS_PATH),
            }
          : undefined
      }
    >
      <Stack gap="var(--space-3)" sx={{ width: "100%" }}>
        <Typography variant="overline">{t("What happens next")}</Typography>
        <List disablePadding>
          {whatHappensNext.map((item) => (
            <ListItem key={item.title} disableGutters alignItems="flex-start">
              <ListItemIcon wrapper="brand">{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} secondary={item.description} />
            </ListItem>
          ))}
        </List>
      </Stack>
    </StatusPage>
  );
};

export default Pending;
